import { Component, OnInit, Inject } from '@angular/core';
import { _ } from '@icc/shared/helpers';
import { PageComponent, SharedFacade, ICC_PAGE_DATA, ModalService } from '@icc/configurator/shared';
import { iccListItem } from '@icc/configurator/ui';
import { IccAccessoryAccessory } from '@icc/construction-elements/types';
import { ConstructColorDto as IccConstructColor } from '@icc/construction-elements/types';
import { AccessoriesListPageComponent, AccessoriesOptionsPageComponent, AccessoriesListPageStoreService } from '@icc/construction-elements/configurator';
import { ActiveSash } from '@icc/window-door/types';
import { AppConfigFactory, APP_CONFIG } from '@icc/shared';
import { ConfigurationsService } from '@icc/configurator/shared';
import { ConfiguratorsDataService, TranslateService } from '@icc/shared';

@Component({
    selector: 'icc-window-accessories-list-page',
    templateUrl: './accessories-list-page.component.html',
    styleUrls: ['./accessories-list-page.component.scss'],
    standalone: false
})
export class WindowAccessoriesListPageComponent extends AccessoriesListPageComponent implements OnInit {
    constructor(
        protected sharedFacade: SharedFacade,
        protected modalService: ModalService,
        protected translateService: TranslateService,
        protected configurationsService: ConfigurationsService<'door'>,
        protected configuratorsDataService: ConfiguratorsDataService,
        @Inject(APP_CONFIG) public config: AppConfigFactory,
        protected accessoriesListPageStoreService: AccessoriesListPageStoreService,
        @Inject(ICC_PAGE_DATA)
        protected pageData: {
            accessories: IccAccessoryAccessory[];
            categories: any[];
            subcategories: any[];
            forIt: 'configuration' | 'sash' | 'side' | 'drive';
            sashNum?: number;
            sash?: ActiveSash | string;
            colors?: IccConstructColor[];
            noPrice: boolean;
        }
    ) {
        super(
            sharedFacade,
            modalService,
            translateService,
            configurationsService,
            configuratorsDataService,
            config,
            accessoriesListPageStoreService,
            pageData
        );


        let subtitle = '';
        switch (pageData.forIt) {
            case 'configuration':
                subtitle = this.translateService.instant('ACCESSORY|całej konstrukcji');
                break;
            case 'sash':
                subtitle =
                    this.translateService.instant('ACCESSORY|kwatery ID:') + ' ' + pageData.sashNum;
                break;
            case 'side':
                switch (pageData.sash) {
                    case 'left':
                        subtitle = this.translateService.instant('ACCESSORY|lewej krawędzi');
                        break;
                    case 'right':
                        subtitle = this.translateService.instant('ACCESSORY|prawej krawędzi');
                        break;
                    case 'top':
                        subtitle = this.translateService.instant('ACCESSORY|górnej krawędzi');
                        break;
                    case 'bottom':
                        subtitle = this.translateService.instant('ACCESSORY|dolnej krawędzi');
                        break;
                }
                break;
            case 'drive':
                subtitle = this.translateService.instant('ACCESSORY|sterowania rolety');
                break;
        }

        this.title =
            this.translateService.instant('ACCESSORY|Wybierz akcesoria dla') + ' ' + subtitle;
    }

    ngOnInit() {
        super.ngOnInit();
        const selectedSideAccessories = this.configurationsService.conf.Current.SideAccessories;
        const selectedMidAccessories = this.configurationsService.conf.Current.Sashes;

        Object.values(selectedSideAccessories || {}).forEach((side) => {
            if (Array.isArray(side)) {
                this.disableAccessories(side);
            }
        })

        if (selectedMidAccessories?.length) {
            selectedMidAccessories.forEach((acc) => {
                this.disableAccessories(acc.hardware);
            })
        }

        if (this.pageData.forIt === 'sash' && this.pageData.sash) {
            const currentSash = this.configurationsService.conf.Current.Sashes
                ?.find(s => s.id === (this.pageData.sash as ActiveSash).id);
            if (currentSash) {
                this.disableAccessoriesPerSash(currentSash.hardware);
            }
        }

        const dependencies = this.configuratorsDataService.data.dependencies || [];
        const blockedAccessoriesToConfiguration = this.configurationsService.conf.Current.Dependencies?.filter(
            (p) => p.type === 'blockade_to_configuration' || p.type === 'blockadeAccessory'
        );
        if (blockedAccessoriesToConfiguration && dependencies) {
            this.markAccessoryAsDisabledBasedOnDependencyBlockingToConfiguration(
                blockedAccessoriesToConfiguration,
                dependencies
            );
        }
    }

    getSubcategoriesFromParentCategoryBasedOnId(id: number) {
        const parentCategories = this.configuratorsDataService.data.windowAccessoriesCategories.filter(
            (c: any) => c.parent_id
        );

        if (!parentCategories.length) {
            return [];
        }

        return this.accessoriesListPageStoreService.getCategories(id, parentCategories);
    }

    selectAccessory(item: iccListItem) {
        const accessory = this.pageData.accessories.find((a) => Number(a.id) === Number(item.id));
        if (accessory) {
            this.modalService
                .open({
                    pageComponent: AccessoriesOptionsPageComponent,
                    resolve: {
                        accessory,
                        forIt: this.pageData.forIt,
                        sash: this.pageData.sash,
                        colors: this.pageData.colors,
                    },
                })
                .result.then((result) => {
                    if (result) {
                        this.sharedFacade.closePage(result);
                    }
                });
        }
    }

    markAccessoryAsDisabledBasedOnDependencyBlockingToConfiguration(
        blockedAccessoriesToConfiguration,
        dependencies
    ) {
        dependencies.forEach((dependency) => {
            blockedAccessoriesToConfiguration.forEach((blocked) => {
                if (dependency && blocked && Number(dependency.id) === Number(String(blocked.id).split('-')[0])) {
                    if(blocked.type === 'blockadeAccessory') {
                        const dependencyAction = dependency.actions.find((p: any) => p.type === 'blockadeAccessory');
                        const accessoriesId = dependency.actions.find((p) => p.type === 'blockadeAccessory')?.value || [];
                         const hideAccessory = dependencyAction?.valueType === 'hide' || false;
                        if(hideAccessory) {
                            this.accessories = this.accessories.filter((p) => !accessoriesId.map(Number).includes(Number(p.id)));
                        }  else {
                            accessoriesId.forEach((accessoryId) => {
                                this.disableAccessoryAndAddDescription(accessoryId);
                            });
                        }
                    } else {
                        dependency.conditions.forEach((condition) => {
                            if (condition.type === 'accessory') {
                                const accessory = this.accessories.find(
                                    (p) => p.id === Number(condition.value)
                                );
                                if (accessory && condition.value) {
                                    this.disableAccessoryAndAddDescription(condition.value);
                                }
                            }
                            if (condition.type === 'sashAccessory') {
                                const accessory = this.accessories.find(
                                    (p) => p.id === Number(condition.value)
                                );
                                if (accessory && condition.value) {
                                    this.disableAccessoryAndAddDescription(condition.value);
                                }
                            }
                        });
                    }
                }
            });
        });
    }

    disableAccessoryAndAddDescription(id) {
        const accessory =  this.accessories.find((p) => p.id === Number(id));
        if(accessory) {
            accessory.disabled = true;
            accessory.description += this.translateService.instant(
                'ACCESSORY|Nie można dodać dodatku do konstrukcji'
            );
        }
    }
}
